import { Request } from '~src/httpClient/serverApi/requests/Request';
import { IGetItemsParams } from '~src/httpClient/serverApi/types/IGetItemsParams';
import { IGetItemsResult } from '~src/httpClient/serverApi/types/IGetItemsResult';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';

export interface IResourceGovernanceSettings {
  extractsConcurrencyLimit: number | undefined;
  subscriptionsConcurrencyLimit: number | undefined;
  flowsConcurrencyLimit: number | undefined;
  runtimeLimit: number | undefined;
}

export function isResourceGovernanceSettingLower(current: Partial<IResourceGovernanceSettings>, next: Partial<IResourceGovernanceSettings>): boolean {
  // Undefined is no limit, so if current is undefined then any non underfined next value is lower
  // If current is defined then either next is too (in which case > works normally) or next is undefined and thus larger
  // this does not require an additional check because [X] > undefined is false
  const isLimitLower = (current: number | undefined, next: number | undefined) => (current === undefined && next !== undefined) || current > next;
  return isLimitLower(current?.extractsConcurrencyLimit, next?.extractsConcurrencyLimit)
    || isLimitLower(current?.subscriptionsConcurrencyLimit, next?.subscriptionsConcurrencyLimit)
    || isLimitLower(current?.flowsConcurrencyLimit, next?.flowsConcurrencyLimit)
    || isLimitLower(current?.runtimeLimit, next?.runtimeLimit);
}

export type IResourceGovernanceSettingsKey = keyof IResourceGovernanceSettings;
export type IResourceGovernanceServerSettingsKey = keyof IResourceGovernanceServerSettings;

export interface IResourceGovernanceSiteSetting extends IExplorableItemTypeInfo<'resourceGovernanceSetting'>, IResourceGovernanceSettings {
  siteId: string;
  siteName: string;
  siteUrlName: string;
  actualRunTime: number; // In seconds
  isDefault: boolean;
}

export interface IResourceGovernanceServerSettings extends IResourceGovernanceSettings {
  nextResetAt: number; // Minutes past midnight; TODO: Subject to more discussion
}

export interface IGetResourceGovernanceSiteSettingsResult extends IGetItemsResult {
  settings: Omit<IResourceGovernanceSiteSetting, 'contentType' | 'id'>[];
}
export class GetResourceGovernanceSiteSettingsRequest extends Request<IGetItemsParams, IGetResourceGovernanceSiteSettingsResult> {
  constructor(params: IGetItemsParams) {
    super('getResourceGovernanceSiteSettings', params);
  }
}

export type IResourceGovernanceSiteSettingUpdate = {
  siteIds: string[];
} & Omit<IResourceGovernanceSiteSetting, 'id' | 'siteId' | 'siteName' | 'siteUrlName' | 'actualRunTime' | 'contentType'>;
export class UpdateResourceGovernanceSiteSettingsRequest extends Request<IResourceGovernanceSiteSettingUpdate, IResult> {
  constructor(params: IResourceGovernanceSiteSettingUpdate) {
    super('updateResourceGovernanceSiteSettings', params);
  }
}

export interface IGetResourceGovernanceServerSettingsResult extends IResult, IResourceGovernanceServerSettings {}
export class GetResourceGovernanceServerSettingsRequest extends Request<{}, IGetResourceGovernanceServerSettingsResult> {
  constructor() {
    super('getResourceGovernanceServerSettings', {});
  }
}

export type ResourceGovernanceServerSettingsUpdate = {
  resourceGovernanceServerSettings: IResourceGovernanceServerSettings;
}
export class UpdateResourceGovernanceServerSettingsRequest extends Request<ResourceGovernanceServerSettingsUpdate, IResult> {
  constructor(params: ResourceGovernanceServerSettingsUpdate) {
    super('updateResourceGovernanceServerSettings', params);
  }
}
